import { Grid, Box, Typography } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Dropmenu, Search } from '../../Components';
import { Link, useNavigate } from 'react-router-dom';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { API_URL } from '../../Config';
import PostAddIcon from '@mui/icons-material/PostAdd';
import Loading from '../../Components/Loading';
import axios from 'axios';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import StyledDashboardLayout from '../style';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useGetDiscountsQuery, useDeleteDiscountMutation } from '../../Redux/Service/DiscountApi';

const DiscountList = () => {
   const MySwal = withReactContent(Swal);
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const [searchInput, setSearchInput] = useState('');
   const [searchQuery, setSearchQuery] = useState('');
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);

   const {
      data: discounts,
      isLoading,
      isError,
      error,
      refetch,
   } = useGetDiscountsQuery({
      limit: rowsPerPage,
      page: page,
      search: searchQuery,
   });

   const [deleteDiscount] = useDeleteDiscountMutation();

   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 15,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
         },
      },
   };

   // Debounce searchInput
   useEffect(() => {
      const timer = setTimeout(() => {
         setSearchQuery(searchInput);
         setPage(1);
      }, 500);

      return () => clearTimeout(timer);
   }, [searchInput]);

   useEffect(() => {
      if (!isLoading && !isError) {
         setLoading(false);
      }
   }, [isLoading, isError]);

   const handleEdit = (id) => {
      navigate(`/discount-edit/${id}`);
   };

   const handleView = (id) => {
      navigate(`/discount/${id}`);
   };

   const handleDelete = async (id) => {
      const result = await MySwal.fire({
         title: 'Are you sure?',
         text: 'Are you sure you want to delete this Discount?',
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
         await deleteDiscount(id).unwrap();
         await MySwal.fire('Deleted!', 'Discount has been deleted.', 'success');
         refetch();
      } catch (error) {
         console.error('Error deleting discount:', error);
         const msg = error?.data?.message || 'Error deleting discount';
         await MySwal.fire('Error!', msg, 'error');
      }
   };

   const handleSearch = useCallback((search) => {
      setSearchInput(search);
   }, []);

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => (page - 1) * rowsPerPage + index + 1,
         button: false,
      },
      { name: 'Name', selector: (row) => row.name, sortable: true, wrap: true, width: '150px' },
      { name: 'Type', selector: (row) => row.type, sortable: true },
      {
         name: 'Value',
         selector: (row) => (row.type === 'percentage' ? `${row.value}%` : row.value),
         sortable: true,
      },
      {
         name: 'Description',
         selector: (row) => row.description || '-',
         wrap: true,
         width: '200px',
      },
      {
         name: 'Active',
         selector: (row) => (row.is_active ? 'Active' : 'Inactive'),
      },
      {
         name: 'Created By',
         selector: (row) => row.created_by || '-',
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {row.created_by || '-'}
            </div>
         ),
      },
      {
         name: 'Created At',
         selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '-'),
         sortable: true,
      },
      {
         name: 'Action',
         cell: (row) => (
            <div>
               <Dropmenu
                  handleEdit={() => handleEdit(row.id)}
                  handleView={() => handleView(row.id)}
                  handleDelete={() => handleDelete(row.id)}
               />
            </div>
         ),
         button: false,
      },
   ];

   if (loading) return <Loading />;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Discounts</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/discount-add" className="btn addBtn">
                           <PostAddIcon /> Add Discount
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card m-3 customCard">
               {isError ? (
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        gap: 2,
                        backgroundColor: '#fee',
                        borderRadius: 2,
                        border: '1px solid #fcc',
                        p: 4,
                     }}
                  >
                     <Typography
                        variant="body2"
                        sx={{ color: '#666', textAlign: 'center', maxWidth: 400 }}
                     >
                        {error?.data?.message ||
                           error?.message ||
                           'Something went wrong while fetching discounts. Please try again.'}
                     </Typography>
                  </Box>
               ) : (
                  <div>
                     <Grid container spacing={2}>
                        <Grid item md={6} xs={12}></Grid>
                        <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                           <Search onSearch={(query) => handleSearch(query)} />
                        </Grid>
                     </Grid>

                     <DataTable
                        columns={columns}
                        data={discounts?.response?.data || []}
                        pagination
                        paginationServer
                        paginationTotalRows={discounts?.response?.total || 0}
                        paginationDefaultPage={page}
                        paginationPerPage={rowsPerPage}
                        onChangePage={(p) => setPage(p)}
                        onChangeRowsPerPage={(newPerPage) => {
                           setRowsPerPage(newPerPage);
                           setPage(1);
                        }}
                        customStyles={customStyles}
                        className="dataTable"
                        progressPending={isLoading}
                        progressComponent={
                           <Box sx={{ py: 4, textAlign: 'center' }}>
                              <Typography>Loading data...</Typography>
                           </Box>
                        }
                        noDataComponent={
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 py: 6,
                                 gap: 1,
                              }}
                           >
                              <Typography variant="h6" sx={{ color: '#999' }}>
                                 No discounts found
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#bbb' }}>
                                 Try adjusting your search criteria
                              </Typography>
                           </Box>
                        }
                     />
                  </div>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
};

export default DiscountList;
